/**
 * CONTACT PAGE — GSAP Animations
 * Monsieur Aure — Editorial Motion Design
 *
 * Dependencies: GSAP 3.12+ & ScrollTrigger (loaded by about-closing section)
 * All animations scoped to .contact-* selectors.
 * Respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function waitForGSAP(cb) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      cb();
    } else {
      setTimeout(function () { waitForGSAP(cb); }, 100);
    }
  }

  function splitWords(el) {
    var words = el.textContent.trim().split(/\s+/);
    el.innerHTML = '';
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'word';
      span.textContent = word;
      el.appendChild(span);
      if (i < words.length - 1) {
        el.appendChild(document.createTextNode(' '));
      }
    });
    return el.querySelectorAll('.word');
  }

  function splitChars(el) {
    var text = el.textContent;
    el.innerHTML = '';
    for (var i = 0; i < text.length; i++) {
      var span = document.createElement('span');
      span.className = 'char';
      span.textContent = text[i] === ' ' ? '\u00A0' : text[i];
      el.appendChild(span);
    }
    return el.querySelectorAll('.char');
  }

  /* ------------------------------------------------------------------ */

  function init() {
    gsap.registerPlugin(ScrollTrigger);

    if (prefersReducedMotion) {
      revealAll();
      return;
    }

    initHero();
    initInfo();
    initFormSection();
    initClosing();
  }

  function revealAll() {
    var selectors = [
      '.contact-hero__label',
      '.contact-hero__subtext',
      '.contact-hero__aside',
      '.contact-info__item',
      '.contact-form-section__label',
      '.contact-form-section__desc',
      '.contact-form-section__hours',
      '.contact-form-section__quote',
      '.contact-form-elegant',
      '.contact-closing__text',
      '.contact-closing__cta'
    ];
    document.querySelectorAll(selectors.join(',')).forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.clipPath = 'none';
    });
    document.querySelectorAll('.word, .char').forEach(function (el) {
      el.style.opacity = '1';
      el.style.clipPath = 'none';
      el.style.transform = 'none';
    });
    document.querySelectorAll('.contact-hero__rule, .contact-form-section__rule, .contact-closing__rule').forEach(function (el) {
      el.style.transform = 'scaleX(1)';
    });
  }

  /* ------------------------------------------------------------------ */
  /*  1 — Hero                                                           */
  /* ------------------------------------------------------------------ */

  function initHero() {
    var heroes = document.querySelectorAll('.contact-hero');
    heroes.forEach(function (hero) {
      var label = hero.querySelector('.contact-hero__label');
      var heading = hero.querySelector('.contact-hero__heading');
      var rule = hero.querySelector('.contact-hero__rule');
      var subtext = hero.querySelector('.contact-hero__subtext');
      var aside = hero.querySelector('.contact-hero__aside');

      // Word mask reveal on heading
      if (heading) {
        var words = splitWords(heading);
        gsap.to(words, {
          clipPath: 'inset(0 0 0% 0)',
          duration: 0.7,
          stagger: 0.09,
          ease: 'power2.out',
          delay: 0.2
        });
      }

      // Label fade
      if (label) {
        gsap.to(label, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          delay: 0.1
        });
        gsap.set(label, { opacity: 0, y: 12 });
      }

      // Rule expand
      if (rule) {
        gsap.to(rule, {
          scaleX: 1,
          duration: 0.9,
          ease: 'power2.out',
          delay: 0.7
        });
      }

      // Subtext reveal
      if (subtext) {
        gsap.to(subtext, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          delay: 0.9
        });
        gsap.set(subtext, { opacity: 0, y: 20 });
      }

      // Aside decorative element
      if (aside) {
        gsap.to(aside, {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power2.out',
          delay: 0.6
        });
        gsap.set(aside, { opacity: 0, x: 30 });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  2 — Info Strip                                                     */
  /* ------------------------------------------------------------------ */

  function initInfo() {
    var items = document.querySelectorAll('.contact-info__item');
    items.forEach(function (item, i) {
      gsap.to(item, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        delay: i * 0.12,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: item,
          start: 'top 88%',
          toggleActions: 'play none none none'
        }
      });
      gsap.set(item, { opacity: 0, y: 25 });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  3 — Form Section                                                   */
  /* ------------------------------------------------------------------ */

  function initFormSection() {
    var sections = document.querySelectorAll('.contact-form-section');
    sections.forEach(function (section) {
      var label = section.querySelector('.contact-form-section__label');
      var heading = section.querySelector('.contact-form-section__heading');
      var desc = section.querySelector('.contact-form-section__desc');
      var rule = section.querySelector('.contact-form-section__rule');
      var hours = section.querySelector('.contact-form-section__hours');
      var quote = section.querySelector('.contact-form-section__quote');
      var form = section.querySelector('.contact-form-elegant');

      var trigger = { trigger: section, start: 'top 68%', toggleActions: 'play none none none' };

      // Label
      if (label) {
        gsap.to(label, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', scrollTrigger: trigger });
        gsap.set(label, { opacity: 0, y: 12 });
      }

      // Heading word reveal
      if (heading) {
        var words = splitWords(heading);
        gsap.to(words, {
          clipPath: 'inset(0 0 0% 0)',
          duration: 0.6,
          stagger: 0.08,
          ease: 'power2.out',
          delay: 0.15,
          scrollTrigger: trigger
        });
      }

      // Description
      if (desc) {
        gsap.to(desc, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', delay: 0.4, scrollTrigger: trigger });
        gsap.set(desc, { opacity: 0, y: 20 });
      }

      // Rule
      if (rule) {
        gsap.to(rule, { scaleX: 1, duration: 0.8, ease: 'power2.out', delay: 0.5, scrollTrigger: trigger });
      }

      // Hours
      if (hours) {
        gsap.to(hours, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', delay: 0.65, scrollTrigger: trigger });
        gsap.set(hours, { opacity: 0, y: 15 });
      }

      // Quote
      if (quote) {
        gsap.to(quote, { opacity: 0.7, y: 0, duration: 0.8, ease: 'power2.out', delay: 0.75, scrollTrigger: trigger });
        gsap.set(quote, { opacity: 0, y: 15 });
      }

      // Form
      if (form) {
        gsap.to(form, { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out', delay: 0.3, scrollTrigger: trigger });
        gsap.set(form, { opacity: 0, y: 40 });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  4 — Closing                                                        */
  /* ------------------------------------------------------------------ */

  function initClosing() {
    var sections = document.querySelectorAll('.contact-closing');
    sections.forEach(function (section) {
      var rule = section.querySelector('.contact-closing__rule');
      var heading = section.querySelector('.contact-closing__heading');
      var text = section.querySelector('.contact-closing__text');
      var cta = section.querySelector('.contact-closing__cta');

      var trigger = { trigger: section, start: 'top 72%', toggleActions: 'play none none none' };

      // Rule
      if (rule) {
        gsap.to(rule, { scaleX: 1, duration: 0.8, ease: 'power2.out', scrollTrigger: trigger });
      }

      // Heading char reveal
      if (heading) {
        var chars = splitChars(heading);
        gsap.set(chars, { opacity: 0, y: 12 });
        gsap.to(chars, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.02,
          ease: 'power2.out',
          delay: 0.2,
          scrollTrigger: trigger
        });
      }

      // Text
      if (text) {
        gsap.to(text, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', delay: 0.5, scrollTrigger: trigger });
        gsap.set(text, { opacity: 0, y: 15 });
      }

      // CTA
      if (cta) {
        gsap.to(cta, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', delay: 0.7, scrollTrigger: trigger });
        gsap.set(cta, { opacity: 0, y: 12 });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Shopify Theme Editor                                               */
  /* ------------------------------------------------------------------ */

  function handleEditorReload() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function () {
        ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
        init();
      });
    }
  }

  function boot() {
    waitForGSAP(function () {
      init();
      handleEditorReload();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
